import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

const EMPTY_FORM = {
  cui: "",
  denumire: "",
  adresa: "",
  judet: "",
  localitate: "",
  nr_reg_com: "",
  telefon: "",
  email: "",
  persoana_contact: "",
  observatii: "",
};

export default function NewClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchFromANAF = async () => {
    const cui = form.cui.trim();
    if (!cui) {
      toast.error("Introdu mai întâi CUI-ul");
      return;
    }
    setFetching(true);
    try {
      const { data } = await api.get(`/anaf/${cui}`);
      setForm((f) => ({
        ...f,
        cui: data.cui || cui,
        denumire: data.denumire || f.denumire,
        adresa: data.adresa || f.adresa,
        judet: data.judet || f.judet,
        localitate: data.localitate || f.localitate,
        nr_reg_com: data.nr_reg_com || f.nr_reg_com,
        telefon: data.telefon || f.telefon,
      }));
      toast.success("Date preluate de la ANAF");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare ANAF");
    } finally {
      setFetching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cui.trim() || !form.denumire.trim()) {
      toast.error("CUI și denumirea sunt obligatorii");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", form);
      toast.success("Client adăugat");
      setForm(EMPTY_FORM);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setForm(EMPTY_FORM);
      }}
    >
      <DialogContent className="w-[92vw] max-w-md sm:max-w-lg max-h-[85dvh] xl:max-h-[90dvh] overflow-y-auto p-4 xl:p-5">
        <DialogHeader className="space-y-1">
          <DialogTitle className="font-display text-lg xl:text-xl">Client nou</DialogTitle>
          <DialogDescription className="text-xs">
            Introdu CUI-ul, apoi apasă <b>Preia din ANAF</b>. Restul câmpurilor se completează automat.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-2.5">
          <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label htmlFor="tb-cui" className="label-eyebrow text-[10px]">CUI *</Label>
              <Input
                id="tb-cui"
                value={form.cui}
                onChange={(e) => setForm({ ...form, cui: e.target.value })}
                placeholder="ex: RO14399840 sau 14399840"
                className="mt-1 h-9"
                required
                data-testid="topbar-client-cui-input"
              />
            </div>
            <Button
              type="button"
              onClick={fetchFromANAF}
              disabled={fetching || !form.cui}
              variant="outline"
              size="sm"
              className="h-9 border-gold text-gold-deep hover:bg-gold hover:text-white transition-colors"
              data-testid="topbar-fetch-anaf-btn"
            >
              {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              Preia din ANAF
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-2.5">
            <div className="sm:col-span-2">
              <Label className="label-eyebrow text-[10px]">Denumire firmă *</Label>
              <Input
                value={form.denumire}
                onChange={(e) => setForm({ ...form, denumire: e.target.value })}
                className="mt-1 h-9"
                required
                data-testid="topbar-client-denumire-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-eyebrow text-[10px]">Adresă</Label>
              <Input value={form.adresa} onChange={(e) => setForm({ ...form, adresa: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Județ</Label>
              <Input value={form.judet} onChange={(e) => setForm({ ...form, judet: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Localitate</Label>
              <Input value={form.localitate} onChange={(e) => setForm({ ...form, localitate: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Nr. Reg. Com.</Label>
              <Input value={form.nr_reg_com} onChange={(e) => setForm({ ...form, nr_reg_com: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Telefon</Label>
              <Input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Persoană contact</Label>
              <Input value={form.persoana_contact} onChange={(e) => setForm({ ...form, persoana_contact: e.target.value })} className="mt-1 h-9" />
            </div>
            <div className="sm:col-span-2">
              <Label className="label-eyebrow text-[10px]">Observații</Label>
              <Textarea value={form.observatii} onChange={(e) => setForm({ ...form, observatii: e.target.value })} className="mt-1 min-h-0" rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>Anulează</Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving}
              className="btn-gold hover:btn-gold"
              data-testid="topbar-save-client-btn"
            >
              {saving ? "Se salvează..." : "Salvează client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
